/**
 * `date_format_inference` — lo que el backend contesta cuando MÁS DE UN formato de fecha explica el
 * archivo entero (`01/02/2026` es DD/MM/YYYY y MM/DD/YYYY a la vez).
 *
 * En ese caso el backend deja `date_format` en `null` A PROPÓSITO y manda los candidatos con un
 * valor de ejemplo:
 *
 *     {"date_format": null, "candidates": ["MM/DD/YYYY", "DD/MM/YYYY"],
 *      "sample": "01/02/2026", "ambiguous": true}
 *
 * Elegir uno nosotros es justo lo que no hay que hacer: si erramos, TODAS las filas entran con la
 * fecha cambiada y nadie se entera. Pero dejar el combo en `-----` sin decir nada es el mismo
 * silencio que la inferencia venía a eliminar. La UI pregunta.
 *
 * REGLA DE LA CASA: lo que viene del backend nunca indexa un catálogo sin fallback. Un candidato que
 * este archivo no sabe interpretar se DESCARTA — ofrecer un botón que setea un valor que el
 * `DateFormatSelect` no tiene sería peor que no preguntar. Si quedan menos de dos, no hay pregunta
 * que hacer y todo se comporta como antes de la inferencia (backend viejo incluido: la clave
 * simplemente no viaja y llega `undefined`).
 */

/**
 * Los formatos que el frontend sabe LEER, con dónde cae cada parte.
 *
 * Es el mismo trío que ofrece `DateFormatSelect.dateCurrencyFormats` y hay un test guardián que lo
 * fija: un formato nuevo en el select sin entrada acá quedaría fuera de la pregunta en silencio.
 */
const KNOWN_FORMATS = {
    'YYYY-MM-DD': {pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: ['year', 'month', 'day']},
    'MM/DD/YYYY': {pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: ['month', 'day', 'year']},
    'DD/MM/YYYY': {pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: ['day', 'month', 'year']}
};

export const INTERPRETABLE_DATE_FORMATS = Object.keys(KNOWN_FORMATS);

/**
 * Qué fecha sería `sample` si el archivo estuviera en `format`. `null` si ese formato no lo explica
 * (o si el formato no es uno de los que sabemos leer).
 *
 * Es lo que convierte la pregunta en contestable: nadie elige entre `DD/MM/YYYY` y `MM/DD/YYYY`,
 * pero cualquiera elige entre "1 de febrero" y "2 de enero".
 */
export function readSampleAs(sample, format) {
    const spec = KNOWN_FORMATS[format];
    if (!spec || typeof sample !== 'string') return null;

    const match = spec.pattern.exec(sample.trim());
    if (!match) return null;

    const parts = {};
    spec.order.forEach((name, index) => {
        parts[name] = Number(match[index + 1]);
    });

    // `new Date(2026, 1, 31)` devuelve el 3 de marzo sin quejarse: si la fecha no vuelve igual, ese
    // formato no explica el valor y no se ofrece como opción.
    const date = new Date(parts.year, parts.month - 1, parts.day);
    const isSameDate = date.getFullYear() === parts.year
        && date.getMonth() === parts.month - 1
        && date.getDate() === parts.day;

    return isSameDate ? date : null;
}

/**
 * Normaliza el `date_format_inference` del backend a `{sample, candidates}`, o `null` cuando no hay
 * nada que preguntar (clave ausente, no ambiguo, sin ejemplo, o menos de dos candidatos legibles).
 */
export function parseDateFormatInference(inference) {
    if (!inference || typeof inference !== 'object') return null;
    if (inference.ambiguous !== true) return null;

    const sample = typeof inference.sample === 'string' ? inference.sample.trim() : '';
    if (sample === '') return null;

    const rawCandidates = Array.isArray(inference.candidates) ? inference.candidates : [];
    const candidates = rawCandidates.filter((format) => readSampleAs(sample, format) !== null);

    // Con un solo candidato legible no hay pregunta: sería pedirle al usuario que confirme lo obvio.
    if (candidates.length < 2) return null;

    return {sample: sample, candidates: candidates};
}
